import Grid from "components/primitives/Grid";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DownloadIcon from "@mui/icons-material/Download";
import Mui5 from "components/mui5";
import useExercises from "providers/ExercisesProvider/useExercises";
import { useCallback, useMemo } from "react";
import { Exercise } from "types";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import { exerciseAutocompleteFilterOptions } from "config/autocomplete";
import { useTranslation } from "react-i18next";
import { firstBy } from "thenby";
import Div from "components/primitives/Div";
import useNaturalSort from "hooks/useNaturalSort";

interface TemplateBuilderProps {
  selectedExerciseIds: string[];
  onExercisesChange: (exerciseIds: string[]) => void;
  onDownloadTemplate: () => void;
  isDownloadDisabled: boolean;
  imperialSystemEnabled: boolean;
}

function TemplateBuilder(props: TemplateBuilderProps) {
  const { selectedExerciseIds, onExercisesChange, onDownloadTemplate, isDownloadDisabled, imperialSystemEnabled } =
    props;
  const { t } = useTranslation();
  const naturalCompare = useNaturalSort();
  const exercises = useExercises();

  const oneRepMaxExercises = useMemo(() => exercises.filter(ExerciseHelpers.isOneRepMaxExercise), [exercises]);

  const sortedOneRepMaxExercises = useMemo(
    () =>
      oneRepMaxExercises
        .slice()
        .sort(firstBy("category", { cmp: naturalCompare }).thenBy("name", { cmp: naturalCompare })),
    [naturalCompare, oneRepMaxExercises]
  );

  const selectedExercises = useMemo(
    () => oneRepMaxExercises.filter((exercise) => selectedExerciseIds.includes(exercise.id)),
    [oneRepMaxExercises, selectedExerciseIds]
  );

  const renderInputValue = useCallback(
    (params: any) => (
      <Mui5.TextField
        {...params}
        margin="dense"
        label={t("athlete.bulk_1rm_select_exercises")}
        variant="outlined"
        color="primary"
        size="small"
        fullWidth
      />
    ),
    [t]
  );

  const renderOptionValue = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: Exercise, { selected }: { selected: boolean }) => (
      <MenuItem {...props} key={option.id} aria-label={t("global.select_option_label", { label: option.name })}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          {selected ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
        </ListItemIcon>
        {option.name}
      </MenuItem>
    ),
    [t]
  );

  const autocompleteOnChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, autocompleteOptions: Exercise[]) => {
      const exerciseIds = autocompleteOptions.map((option) => option.id);
      onExercisesChange(exerciseIds);
    },
    [onExercisesChange]
  );

  const getOptionLabelValue = useCallback((option: Exercise) => option.name, []);

  const groupByValue = useCallback((option: Exercise) => option.category, []);

  const unit = useMemo(() => (imperialSystemEnabled ? "LBS" : "KG"), [imperialSystemEnabled]);

  return (
    <Div sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Div>
        <Typography variant="h6" gutterBottom>
          {t("global.instructions_label")}
        </Typography>
        <Typography
          variant="body2"
          component="ol"
          sx={{ color: "text.secondary", pl: 2.5, m: 0, listStyleType: "decimal" }}
        >
          <li>
            {t("athlete.bulk_1rm_instructions_step_1_part1")} <strong>{unit}</strong>{" "}
            {t("athlete.bulk_1rm_instructions_step_1_part2")}
          </li>
          <li>{t("athlete.bulk_1rm_instructions_step_2")}</li>
          <li>{t("athlete.bulk_1rm_instructions_step_3")}</li>
          <li>{t("athlete.bulk_1rm_instructions_step_4")}</li>
        </Typography>
      </Div>
      <Grid container spacing={2} alignItems="flex-start" sx={{ width: "100%" }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Mui5.Autocomplete
            multiple
            limitTags={0}
            disableCloseOnSelect
            disableClearable
            options={sortedOneRepMaxExercises}
            value={selectedExercises}
            renderInput={renderInputValue}
            renderOption={renderOptionValue}
            getOptionLabel={getOptionLabelValue}
            groupBy={groupByValue}
            onChange={autocompleteOnChange}
            filterOptions={exerciseAutocompleteFilterOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 1,
              display: "block",
            }}
          >
            {t("athlete.bulk_1rm_exercises_selected", { count: selectedExerciseIds.length })}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Mui5.Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownloadTemplate}
            disabled={isDownloadDisabled}
            fullWidth
          >
            {t("athlete.bulk_1rm_download_template")}
          </Mui5.Button>
        </Grid>
      </Grid>
    </Div>
  );
}

export default TemplateBuilder;
